import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_MOBILE_OR_TABLET_BROWSER, IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import {
    cloudFilesMigrationDesktop,
    TELEGRAM_WEB_START_MODAL_CONFIG,
    TELEGRAM_WEB_START_MODAL_ID,
} from 'reactApp/appHelpers/featuresHelpers/features/cloudFilesMigrationDesktop';
import { getUserFlags } from 'reactApp/modules/profile/profile.selectors';
import { ProfileFlags } from 'reactApp/modules/profile/profile.types';
import { isHomeLikeStorage } from 'reactApp/modules/promoNew/items/helpers/homeLikeStorage.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class TelegramMigrationModel extends BaseModel {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/TelegramMigrationFirstScreenModal/TelegramMigrationFirstScreenModal'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const telegramMigrationFirstScreenModalCreator: ModelCreator = ({ models, render, stores }) => {
    return new TelegramMigrationModel(
        TELEGRAM_WEB_START_MODAL_ID,
        models.base,
        new BaseModelProps(TELEGRAM_WEB_START_MODAL_ID, {
            store: new LocalStorageStore(TELEGRAM_WEB_START_MODAL_ID, stores.localStorage),
            renderer: new ReactRenderer(TELEGRAM_WEB_START_MODAL_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit: TELEGRAM_WEB_START_MODAL_CONFIG.showLimit,
            closeLimit: TELEGRAM_WEB_START_MODAL_CONFIG.closeLimit,
            showInterval: TELEGRAM_WEB_START_MODAL_CONFIG.showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            hideOnCompleted: true,
            hideOnSnooze: true,
            test: (resolve, reject) => {
                if (IS_MOBILE_OR_TABLET_BROWSER || IS_WEBVIEW || IS_WEBVIEW_FROM_UA || !cloudFilesMigrationDesktop) {
                    reject();
                    return;
                }

                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                if (!isHomeLikeStorage(storage)) {
                    reject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isB2BUser = UserSelectors.isB2BUser(state);
                const isMigrationUsed = getUserFlags(state).includes(ProfileFlags.TG_MIGRATION_USED);

                const isReject = isBizUser || isB2BUser || isMigrationUsed;

                sendXray(['promo-test', TELEGRAM_WEB_START_MODAL_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
